<template>
    <div>
        <div class="w-full flex flex-column align-items-center mt-5" style="color: var(--theme-font-color);">
            <div style="height: 2.9rem;"></div>
            <span style="color: var(--theme-font-color); font-size: var(--register-maintitle-font-size); font-weight: var(--register-maintitle-font-weight);">New Email Confirmation</span>

            <div class="w-full flex flex-column align-items-center justify-content-center">
                <div v-if="!isConfirmed">
                    <div v-if="!errorConfirmation" style="height: 65vh;" class="flex align-items-center justify-content-center">
                        <LoadingSpinner/>
                    </div>
                    <div v-else class="w-full flex flex-column align-items-center justify-content-center mt-5" style="font-size: var(--text-font-size); font-weight: var(--text-font-weight);">
                        <SvgError class="mb-4" className="registerconfirm-svg" color="var(--mydanger-color)"/>
                        <span v-if="confirmationErrorMessage" class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ confirmationErrorMessage }}</span>

                        <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                            Please try to
                            <router-link unstyled :to="{ path: '/register'}" style="text-decoration: none;">
                                <span id="go-to-register-text" class="cursor-pointer">sign in</span>
                            </router-link>
                            with your previous email address.
                        </span>
                    </div>
                </div>

                <div v-else class="flex flex-column align-items-center mt-5">
                    <SvgSuccess class="mb-4" className="registerconfirm-svg" color="var(--mysuccess-color)"/>
                    <span class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ successMessage }}</span>

                    <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                        You can now
                        <router-link unstyled :to="{ path: '/login'}" style="text-decoration: none;">
                            <span id="go-to-register-text" class="cursor-pointer">sign in</span>
                        </router-link>
                        with your new email address!
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, watchEffect, onMounted } from 'vue';
// PrimeVue
import SvgSuccess from '@/components/SVGs/SvgSuccess.vue'
import SvgError from '@/components/SVGs/SvgError.vue'
// vue
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
// Others
import { useRouter } from 'vue-router'
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchUpdateEmailConfirm } = useApiRequest()
const httpUpdateEmailConfirmLoading = ref(false)
const httpUpdateEmailConfirmData = ref(null)

const router = useRouter()

// --- PROPS ---
const props = defineProps({
    token: String
})
const { token } = toRefs(props)

// --- REDIRECT IF NO ATTACHED TOKEN ---
watchEffect(() => {
    if (!token.value) { router.push({ path: '/login' }) }
})

// --- VARIABLES ---
const isConfirmed = ref(false)
const errorConfirmation = ref(false)

// --- CONFIRMATION ---
const successMessage = ref("Your account has been successfully linked to your new email address.")
const confirmationErrorMessage = ref(null)
const confirm = async () => {
    confirmationErrorMessage.value = null
    isConfirmed.value = false
    errorConfirmation.value = false
    // Send HTTP request
    httpUpdateEmailConfirmLoading.value = true
    try {
        httpUpdateEmailConfirmData.value = await fetchUpdateEmailConfirm({ token: token.value })
        isConfirmed.value = true
        errorConfirmation.value = false
    } catch (err) {
        handleUpdateEmailConfirmError(err)
    } finally {
        httpUpdateEmailConfirmLoading.value = false
    }
}
// Handle an error from the server
const handleUpdateEmailConfirmError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `New Email Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        errorConfirmation.value = true
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    confirmationErrorMessage.value = "It seems that the request body does not contain the necessary information."
                    //toastService.add({ severity: 'warn', summary: "New Email Confirmation: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `New Email Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // The token attached to the request is not valid : expired = CONFIRMATION_EMAIL_UPDATE_TOKEN_INVALID (401) => toast
                if (errorCode == 'CONFIRMATION_EMAIL_UPDATE_TOKEN_INVALID') {
                    confirmationErrorMessage.value = "It seems that the confirmation link is not valid anymore."
                    //toastService.add({ severity: 'warn', summary: "New Email Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link is not valid anymore. Try to register again.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `New Email Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 410: // Confirmation exists but is expired = CONFIRMATION_EMAIL_UPDATE_TOKEN_INVALID (410) => toast
                if (errorCode == 'CONFIRMATION_EMAIL_UPDATE_TOKEN_INVALID') {
                    confirmationErrorMessage.value = "It seems that the confirmation link is not valid anymore."
                    //toastService.add({ severity: 'warn', summary: "New Email Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link is not valid anymore. Try to register again.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `New Email Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR/INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    confirmationErrorMessage.value = "Sorry, it seems that there is an internal error. Try later.'"
                    //toastService.add({ severity: 'error', summary: "New Email Confirmation: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `New Email Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `New Email Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `New Email Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Make the register_confirm request on mounted ! ---
onMounted(() => {
    confirm()
})
</script>

<style scoped>
:deep(.p-progress-spinner-circle) {
    stroke: var(--myprimary-color);
    animation: p-progress-spinner-dash 1.5s ease-in-out infinite !important;
}
/* Go to register */
#go-to-register-text {
    text-decoration: none;
    color: var(--button-normal-color);
}
#go-to-register-text:visited {
    color: var(--button-normal-color);
}
#go-to-register-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>